import { Interactive, useController } from "@react-three/xr";
import React from "react";
import * as THREE from "three";
import { Hotspot, Popup, TourData } from "../../../../../Types";
import { Center, Resize, Svg } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useCursorShrinkingStore } from "../../UI/CursorNew";
import { useXRPopupStore } from "../XRHotspots";
import { useDataRootStore } from "../../../../../api/Zustand";
import XRHotspotLabel from "./XRHotspotLabel";
import XROpenPopup from "../Open/XROpenPopup";

const root = useDataRootStore.getState().root;

const emptyPop: Popup = {
  id: "0",
  title: "Caricamento",
  text: "Caricamento",
  type: "text",
  media: "none",
  video: undefined,
  image: undefined,
  imageName: undefined,
};

interface HotspotWithPos extends Hotspot {
  pos: THREE.Vector3;
}

let timeout: NodeJS.Timeout;
let timeout2: NodeJS.Timeout;

const XRPopup = (props: {
  pos: number[] | THREE.Vector3;
  hs: Hotspot;
  tourData: TourData;
}) => {
  const xrPop = useXRPopupStore();
  const cursor = useCursorShrinkingStore();

  const currentPop: Popup = React.useMemo(() => {
    return (
      props.tourData.popups.find((one) => one.id === props.hs.id) || emptyPop
    );
  }, [props.hs.id, props.tourData.popups]);

  const currentHS: HotspotWithPos = React.useMemo(() => {
    const thisHs = props.hs;
    const position = new THREE.Vector3(thisHs.x, thisHs.y, thisHs.z);

    return { ...thisHs, pos: position };
  }, [props.hs]);

  const iconRef = React.useRef<THREE.Group>(null);
  const [isHovering, setHoveringIcon] = React.useState<string | null>(null);

  const rightController = useController("right");
  const leftController = useController("left");
  const gazeController = useController("none");

  useFrame(() => {
    if (isHovering === props.hs.id) {
      iconRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
    } else {
      iconRef.current?.scale.lerp(new THREE.Vector3(0.8, 0.8, 0.8), 0.1);
    }
  });

  const thisPopup = React.useMemo(() => {
    return props.tourData.popups.find((one) => one.id === props.hs.target);
  }, [props.hs.target, props.tourData.popups]);

  const openPopup = React.useCallback(() => {
    if (currentHS.target) {
      xrPop.openPop(currentHS.target as string, currentHS.pos.divideScalar(50));
    }
  }, [currentHS.target, currentHS.pos, xrPop]);

  return (
    <>
      {!xrPop.isOpen && (
        <group
          scale={gazeController ? 1.6 : 0.6}
          onUpdate={(self) => self.lookAt(0, 0, 0)}
          position={props.pos as THREE.Vector3}
          visible={!xrPop.isOpen}
        >
          <Center>
            <Interactive
              onSelect={() => {
                setHoveringIcon(null);

                openPopup();
              }}
              onHover={(e) => {
                setHoveringIcon(props.hs.id);

                if (
                  gazeController &&
                  leftController === undefined &&
                  rightController === undefined
                ) {
                  clearTimeout(timeout);
                  clearTimeout(timeout2);

                  timeout = setTimeout(() => {
                    cursor.setShrinking(true);

                    timeout2 = setTimeout(() => {
                      setHoveringIcon(null);
                      cursor.setShrinking(false);

                      openPopup();
                    }, 2e3);
                  }, 1e3);
                }
              }}
              onBlur={() => {
                clearTimeout(timeout);
                clearTimeout(timeout2);
                cursor.setShrinking(false);
                setHoveringIcon(null);
              }}
            >
              {thisPopup?.type === "image" ? (
                <IconFromSvg
                  iconRef={iconRef}
                  url={root + "/svg/imagepopup.svg"}
                />
              ) : thisPopup?.text ? (
                <IconFromSvg iconRef={iconRef} url={root + "/svg/search.svg"} />
              ) : (
                <IconFromSvg iconRef={iconRef} url={root + "/svg/xrpin.svg"} />
              )}

              <mesh name="hotspot" renderOrder={10} visible={false}>
                <planeGeometry args={[20, 20]} />
                <meshBasicMaterial depthTest={false} wireframe />
              </mesh>
            </Interactive>
          </Center>

          <XRHotspotLabel hoverState={isHovering === props.hs.id}>
            {currentPop.title || ""}
          </XRHotspotLabel>
        </group>
      )}

      {xrPop.target === props.hs.id && (
        <group renderOrder={1}>
          <XROpenPopup hs={props.hs} pop={currentPop} />
          {/* {currentPop.type === "image" ? (
            <XRImagePop currentPop={currentPop} hs={props.hs} />
          ) : (
            <XRTextPop currentPop={currentPop} hs={props.hs} />
          )} */}
        </group>
      )}
    </>
  );
};

export default XRPopup;

export const IconFromSvg = (props: { url?: string; iconRef: any }) => {
  if (props.url) {
    return (
      <Center ref={props.iconRef} scale={0.8}>
        <Resize width={true} height={true} scale={30}>
          <Svg
            name="popup"
            src={props.url}
            renderOrder={2}
            fillMaterial={{ color: "#fff", depthTest: false }}
            strokeMaterial={{ color: "#000", depthTest: false }}
            strokeMeshProps={{ position: [0, 0, 1] }}
          />
        </Resize>
      </Center>
    );
  }
};
